/**
 * Holds `refresh` stream actions on a tab whose full-page form submit is in flight.
 *
 * `Session.refresh` skips only while a visit is current. A Drive form submit has no visit until
 * Turbo has read the response body and proposed the follow-up visit. A record-page refresh
 * (docs/adr/0012-live-record-pages.md) landing in that window starts a visit of the current URL, which stops the submission,
 * so the member stays on the page the form was leaving. One landing while a refused response is
 * read replaces the error page.
 *
 * The hold rises at `turbo:submit-start` (frame submits excluded) and falls when Turbo's own
 * current-visit check takes over, when the refused response's page has rendered, or when Turbo will
 * neither visit nor render the response.
 *
 * A held refresh is dropped, not replayed: the redirect target already shows the write. The stream
 * render itself still runs, which keeps `focus-restore-policy.ts`'s stream-render depth balanced.
 *
 * A `refresh` carrying a request id in `session.recentRequests` is this tab's own (docs/adr/0012-live-record-pages.md).
 * It is dropped before Turbo's debounce, which keeps only the last refresh of a window and so would let it replace another refresh.
 */

import type { StreamElement } from "@hotwired/turbo";
import { installSubmissionPhases } from "./submission-phases";

type StreamAction = (this: StreamElement) => void;

/**
 * The part of Turbo's `Session` that runs a page refresh. `refresh` debounces `debouncedRefresh` by
 * `pageRefreshDebouncePeriod`, and that setter rebuilds `refresh`.
 */
export interface RefreshingSession {
   debouncedRefresh?: (url: string, options?: unknown) => void;
   pageRefreshDebouncePeriod: number;
   recentRequests?: { has(requestId: string): boolean };
}

function skipWhileHeld<This, Args extends unknown[]>(
   held: () => boolean,
   run: (this: This, ...args: Args) => void,
): (this: This, ...args: Args) => void {
   return function (this: This, ...args: Args): void {
      if (!held()) {
         run.apply(this, args);
      }
   };
}

function skipOwnRefresh(session: RefreshingSession, run: StreamAction): StreamAction {
   return function (this: StreamElement): void {
      // Libraries/test/mvdmio.ASP.Razor.Tests.Unit/TypeScript/formSubmitRefreshHoldTurbo.test.ts fails when an upgraded Turbo drops recentRequests.
      const requestId = this.requestId;
      if (!requestId || !session.recentRequests?.has(requestId)) {
         run.call(this);
      }
   };
}

/** Re-runs the `pageRefreshDebouncePeriod` setter, which rebuilds `refresh` around the current `debouncedRefresh`. */
function rebuildDebouncedRefresh(session: RefreshingSession): void {
   const period = session.pageRefreshDebouncePeriod;
   session.pageRefreshDebouncePeriod = period;
}

function holdSessionRefresh(session: RefreshingSession, held: () => boolean): () => void {
   const runRefresh = session.debouncedRefresh;
   if (typeof runRefresh !== "function") {
      // Libraries/test/mvdmio.ASP.Razor.Tests.Unit/TypeScript/formSubmitRefreshHoldTurbo.test.ts fails when an upgraded Turbo drops this member.
      return () => {};
   }

   session.debouncedRefresh = skipWhileHeld(held, runRefresh);
   rebuildDebouncedRefresh(session);

   return () => {
      session.debouncedRefresh = runRefresh;
      rebuildDebouncedRefresh(session);
   };
}

/**
 * Installs the hold on Turbo's `StreamActions` and `session`. The hold is checked when the stream
 * action runs and again when the debounced page refresh fires, so a refresh that arrived just
 * before the submit started is held too. Returns an uninstall function (tests).
 */
export function installFormSubmitRefreshHold(
   streamActions: Record<string, StreamAction>,
   session: RefreshingSession,
): () => void {
   const phases = installSubmissionPhases({ scope: "full-page" });
   const held = (): boolean => phases.following;
   const originalRefresh = streamActions.refresh;
   streamActions.refresh = skipOwnRefresh(session, skipWhileHeld(held, originalRefresh));
   const unholdSession = holdSessionRefresh(session, held);

   // Turbo's own current-visit check takes over once any visit starts.
   document.addEventListener("turbo:visit", phases.releaseAll);

   return () => {
      streamActions.refresh = originalRefresh;
      unholdSession();
      document.removeEventListener("turbo:visit", phases.releaseAll);
      phases.uninstall();
   };
}
